import { getGroupingClassNames } from "@myaccount/_components/my-group-members/_client/get-grouping-class-names";
import { useI18n } from "@wojo/localization";
import { Table, Text } from "@wojo/ui";
import {
    ConfirmedRequestedGroupMemberTripsFragment,
    GroupMembersDepartureFragment,
} from "generated/graphql";
import React from "react";
import styles from "../../base-table/BaseTable.module.scss";
import { EmailTravelersButton } from "../../email-travelers-button/EmailTravelersButton";
import { TravelerNameCheckbox } from "../../traveler-name-checkbox/TravelerNameCheckbox";
import { ExtensionRoommates } from "./ExtensionRoommates";
import { TourRoommates } from "./TourRoommates";
import { useEmailSelection } from "@myaccount/_components/my-group-members/_client/hooks/use-email-selection";

export type RoomingTableProps = {
    departure: GroupMembersDepartureFragment;
    bookedTravelers: ConfirmedRequestedGroupMemberTripsFragment[][];
    hasExtension: boolean;
    emailHelpTextId: string;
};

export const RoomingTable: React.FC<RoomingTableProps> = ({
    bookedTravelers,
    departure,
    hasExtension,
    emailHelpTextId,
}) => {
    const i18n = useI18n("account").account.myGroupMembers.roomingTable;
    const { selectedEmailsMap, allEmails, handleCheckboxChange } =
        useEmailSelection({
            confirmedTravelers: bookedTravelers,
        });

    const selectedEmails = Array.from(selectedEmailsMap.values()).filter(
        (email) => email,
    );
    const allRooms = [
        ...departure.rooms,
        ...(departure.activeExtension?.rooms ?? []),
    ];
    const getRoomName = (lineItemCode?: string) => {
        if (!lineItemCode) {
            return i18n.roomNotFound.l();
        }
        return (
            allRooms.find((x) => x.lineItemCode === lineItemCode)?.bed.name ||
            i18n.roomNotFound.l()
        );
    };

    const isSingleRoom = (lineItemCode?: string) => {
        if (!lineItemCode) {
            return false;
        }
        return (
            allRooms.find((x) => x.lineItemCode === lineItemCode)?.bed.name ===
            "Single"
        );
    };

    const notApplicableFragment = () => {
        return (
            <>
                <span aria-hidden={true}>—</span>
                <span className="visually-hidden">
                    {i18n.notApplicable.l()}
                </span>
            </>
        );
    };

    return (
        <>
            <div className={styles.table}>
                <Table
                    aria-describedby={emailHelpTextId}
                    header={
                        <Table.Header>
                            <Table.Cell className={styles["header-cell"]}>
                                {i18n.nameHeader.l()}
                            </Table.Cell>
                            <Table.Cell className={styles["offset-checkbox"]}>
                                {hasExtension
                                    ? i18n.roomingTypeWithExtensionHeader.l()
                                    : i18n.roomingTypeHeader.l()}
                            </Table.Cell>
                            <Table.Cell className={styles["offset-checkbox"]}>
                                {hasExtension
                                    ? i18n.roommateWithExtensionHeader.l()
                                    : i18n.roommateHeader.l()}
                            </Table.Cell>
                            {hasExtension && (
                                <Table.Cell
                                    className={styles["offset-checkbox"]}
                                >
                                    {i18n.extensionRoomHeader.l()}
                                </Table.Cell>
                            )}
                            {hasExtension && (
                                <Table.Cell
                                    className={styles["offset-checkbox"]}
                                >
                                    {i18n.extensionRoommateHeader.l()}
                                </Table.Cell>
                            )}
                        </Table.Header>
                    }
                    showTitle={false}
                    tableTitle={
                        <Text.Heading
                            order="3"
                            style={{ paddingBottom: "var(--g-spacing-md)" }}
                        >
                            {i18n.tableTitle.l()}
                        </Text.Heading>
                    }
                    isStrictStyle={true}
                >
                    {bookedTravelers.map((group) =>
                        group.map((traveler, travelerIndex) => {
                            const email = traveler.customer?.email ?? "";
                            const classNames = getGroupingClassNames(
                                travelerIndex,
                                group.length,
                            );
                            return (
                                <Table.Row
                                    key={traveler._id}
                                    className={classNames}
                                    header={
                                        <Table.Cell className={classNames}>
                                            <TravelerNameCheckbox
                                                id={traveler._id}
                                                email={email}
                                                selectedEmailsMap={
                                                    selectedEmailsMap
                                                }
                                                handleEmailCheckboxChange={
                                                    handleCheckboxChange
                                                }
                                                firstName={
                                                    traveler.customer
                                                        ?.firstName ?? ""
                                                }
                                                lastName={
                                                    traveler.customer
                                                        ?.lastName ?? ""
                                                }
                                            />
                                        </Table.Cell>
                                    }
                                >
                                    <Table.Cell>
                                        {getRoomName(
                                            traveler.openRoomLineItem
                                                ?.lineItemCode,
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {isSingleRoom(
                                            traveler.openRoomLineItem
                                                ?.lineItemCode,
                                        ) ? (
                                            notApplicableFragment()
                                        ) : (
                                            <TourRoommates
                                                traveler={traveler}
                                            />
                                        )}
                                    </Table.Cell>
                                    {hasExtension && (
                                        <>
                                            <Table.Cell>
                                                {traveler.openExtensionRoomLineItem
                                                    ? getRoomName(
                                                          traveler
                                                              .openExtensionRoomLineItem
                                                              .lineItemCode,
                                                      )
                                                    : notApplicableFragment()}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {isSingleRoom(
                                                    traveler.openRoomLineItem
                                                        ?.lineItemCode,
                                                ) ? (
                                                    notApplicableFragment()
                                                ) : (
                                                    <ExtensionRoommates
                                                        traveler={traveler}
                                                    />
                                                )}
                                            </Table.Cell>
                                        </>
                                    )}
                                </Table.Row>
                            );
                        }),
                    )}
                </Table>
            </div>
            <EmailTravelersButton
                emails={selectedEmails}
                allEmails={allEmails}
            />
        </>
    );
};
